import React from 'react';
import { Link } from 'react-router-dom';

const RealEstateDataNotice: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="legal-nav">
        <Link to="/" className="back-link">&larr; Back to Home</Link>
      </div>

      <h1>Real Estate Data Notice</h1>
      <p className="effective-date">Effective date: April 1st, 2024 &middot; Last Updated: March 25th, 2026</p>

      <p>
        This Real Estate Data Notice ("Notice") describes how Closing Theory Studios, Inc. and its affiliated entities ("Closing Theory," "we," "us," or "our") sources, uses, displays, and manages certain real estate-related data and imagery in connection with the Househunt mobile application and any related services, features, content, or experiences (collectively, the "Service").
      </p>
      <p>
        This Notice is intended to provide transparency regarding the sources and treatment of real estate-related content appearing in Househunt. It supplements, and should be read together with, our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
      </p>

      <h2>Definitions</h2>
      <p>The following capitalized terms, as used in this Notice, shall have the meanings set forth below:</p>
      <ul>
        <li><strong>"Closing Theory," "we," "us," or "our"</strong> means Closing Theory Studios, Inc. and its affiliated entities.</li>
        <li><strong>"Househunt"</strong> means the mobile application operated by Closing Theory through which the Service is made available.</li>
        <li><strong>"MLS"</strong> means a Multiple Listing Service, as the term is commonly understood in the real estate industry.</li>
        <li><strong>"Notice"</strong> means this Real Estate Data Notice, as may be amended from time to time.</li>
        <li><strong>"Service"</strong> means the Househunt mobile application and any related services, features, content, or experiences provided by Closing Theory.</li>
        <li><strong>"User" or "you"</strong> means any individual who accesses or uses the Service.</li>
      </ul>

      <h2>1. Purpose of the Service</h2>
      <p>
        Househunt is a game and interactive entertainment experience that incorporates information about real properties for gameplay, commentary, public discussion, and related expressive purposes.
      </p>
      <p>Househunt is <strong>not</strong>:</p>
      <ul>
        <li>a real estate brokerage;</li>
        <li>a Multiple Listing Service or MLS;</li>
        <li>a real estate marketplace;</li>
        <li>a listing syndication platform;</li>
        <li>a solicitation or offer to buy, sell, lease, or finance real property; or</li>
        <li>a substitute for official listing sources, broker advice, or professional real estate services.</li>
      </ul>
      <p>
        Househunt does not provide users with the ability to contact agents, submit purchase inquiries, list or advertise properties for sale or rent, transact in real estate, or use the Service as a marketplace for real property.
      </p>

      <h2>2. Sources of Real Estate Data</h2>
      <p>Real estate-related content appearing in Househunt may come from one or more of the following sources:</p>

      <h3>A. MLS-Sourced Active Listing Data</h3>
      <p>
        For certain markets in which Househunt operates, active listing data is sourced and provided through authorized broker access to applicable MLS platforms. In those markets, access is obtained through participating licensed brokers associated with Closing Theory and/or its affiliated entities, and listing information is accessed through the applicable MLS portals made available in connection with those broker relationships.
      </p>

      <h3>B. Supplemental Third-Party and Publicly Available Sources</h3>
      <p>
        In some cases, supplemental property content may be derived from third-party sources, publicly available online sources, and AI-assisted data compilation tools. Such supplemental content may be used to support gameplay, content completeness, testing, commentary, market coverage, or related Service functions.
      </p>
      <p>Not all property-related content appearing in Househunt is necessarily sourced from an MLS.</p>

      <h2>3. Scope of MLS-Sourced Information Used in the Service</h2>
      <p>
        Where active listing data is sourced and provided from an MLS through authorized broker access, the information available to us may include a broad set of listing details made available within the applicable MLS environment. For gameplay purposes, however, the Service primarily uses and emphasizes selected property attributes such as:
      </p>
      <ul>
        <li>property location;</li>
        <li>active listing price;</li>
        <li>bedroom count;</li>
        <li>bathroom count;</li>
        <li>square footage; and</li>
        <li>property images or photographs.</li>
      </ul>

      <h2>4. Display and Treatment of Listing Information</h2>
      <p>
        In the course of accessing and processing listing data, we may receive or view additional information associated with a listing, including full property addresses, agent names, brokerage names, MLS numbers, and certain contact-related information. However, not all such information is necessarily displayed to users in the Service.
      </p>
      <p>
        Closing Theory may determine, in its discretion, which listing fields, attributes, images, or metadata are displayed, suppressed, excerpted, summarized, ranked, omitted, or incorporated into gameplay elements.
      </p>
      <p>
        The appearance of a property, image, address, or listing-related detail in Househunt does not mean that the property is being marketed through Househunt, that the listing is complete or current, or that the Service reproduces all information contained in any source listing.
      </p>

      <h2>5. Property Images</h2>
      <p>
        Where property images are sourced in connection with MLS-sourced active listings, Househunt may initially display those images in their original, unmodified form as made available through the applicable source.
      </p>
      <p>
        Closing Theory does not represent that users are granted any ownership interest in, or independent reuse rights to, such images beyond their in-app display within the Service.
      </p>

      <h2>6. User-Generated Modifications to Images</h2>
      <p>
        Househunt may permit players or users to create, apply, or share their own edits, overlays, transformations, or other modifications to property images within the Service for their own use and enjoyment. Such edits or modifications are user-generated content and are created by the applicable user, not by Closing Theory.
      </p>
      <p>
        Closing Theory does not claim authorship of, ownership of, or editorial control over user-generated modifications to third-party property images, except to the extent necessary to operate, moderate, display, remove, or enforce our policies within the Service.
      </p>
      <p>
        Users are solely responsible for any modifications they create, upload, publish, or share through the Service. Users may not create or use image modifications in a manner that infringes the rights of any photographer, MLS, broker, property owner, copyright holder, or other third party, or that violates our Terms of Service, community rules, or applicable law.
      </p>
      <p>We reserve the right to review, restrict, disable, or remove user-generated image modifications at any time in our discretion.</p>

      <h2>7. No Brokerage, Agency, or Real Estate Services</h2>
      <p>
        Closing Theory is not engaged through Househunt in the business of brokering real estate transactions, representing buyers or sellers, promoting the sale of specific real estate services, acting as an intermediary, advertising or listing properties for sale or lease, or acting as an agent, broker, or MLS for users of the Service.
      </p>
      <p>
        Any real estate-related content presented in Househunt is presented as part of a game and entertainment experience and for commentary, public discussion, expressive use, and related Service functions. No content in the Service should be understood as:
      </p>
      <ul>
        <li>a real estate listing service offered to the public;</li>
        <li>a brokerage or agency relationship;</li>
        <li>a solicitation to purchase, sell, lease, or finance real estate;</li>
        <li>a referral to a real estate professional;</li>
        <li>property listing services; or</li>
        <li>professional real estate, legal, tax, financial, valuation, or investment advice.</li>
      </ul>

      <h2>8. Accuracy, Completeness, and Timeliness Disclaimer</h2>
      <p>
        Real estate-related information appearing in Househunt may be incomplete, outdated, delayed, approximate, reformatted, simplified, selectively displayed, or otherwise different from information available from official listing sources or licensed professionals.
      </p>
      <p>Without limitation:</p>
      <ul>
        <li>listing status may change quickly;</li>
        <li>prices may change;</li>
        <li>photographs may not reflect current property condition;</li>
        <li>property characteristics may be inaccurate or incomplete;</li>
        <li>displayed content may omit material listing details; and</li>
        <li>gameplay mechanics may alter, summarize, or abstract real-world property information.</li>
      </ul>
      <p>
        Users must not rely on Househunt for home shopping, valuation, brokerage decisions, purchase or sale decisions, investment analysis, legal compliance, or any other real-world real estate purpose. Users seeking current or authoritative real estate information should consult the applicable MLS, listing broker, property owner, or licensed professional.
      </p>

      <h2>9. Reservation of Rights Regarding Service Presentation</h2>
      <p>
        To operate Househunt as a gameplay experience, Closing Theory may, in its discretion and subject to applicable agreements and law, organize, classify, display, excerpt, reorder, summarize, suppress, or otherwise adapt property-related data and imagery for use within the Service.
      </p>
      <p>
        This includes the right to present property-related content in formats designed for gameplay, scoring, commentary, comparison, public opinion features, or other entertainment-oriented functions, rather than in the format in which such content may appear in an originating source.
      </p>

      <h2>10. Rights Holder and Data Source Concerns</h2>
      <p>
        If you are an MLS, broker, agent, photographer, copyright owner, property owner, data provider, or other rights holder and you believe that content appearing in Househunt is inaccurate, unauthorized, infringing, improperly attributed, outdated, or otherwise objectionable, please contact us at:
      </p>
      <p><a href="mailto:legal@closingtheory.com">legal@closingtheory.com</a></p>
      <p>Please include enough detail for us to identify the content at issue and review your request, such as:</p>
      <ul>
        <li>the property address or identifying information;</li>
        <li>the image(s) or content at issue;</li>
        <li>the basis for your concern or claim;</li>
        <li>your relationship to the content or property; and</li>
        <li>any supporting documentation reasonably necessary for review.</li>
      </ul>
      <p>We reserve the right to investigate reported issues and to remove, restrict, correct, or update content in our discretion.</p>

      <h2>11. Governing Law and Jurisdiction</h2>
      <p>
        This Notice and any disputes arising out of or relating to its subject matter shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict-of-laws principles. To the extent that any claim or dispute arising under or in connection with this Notice is not otherwise subject to binding arbitration under our Terms of Service, the exclusive jurisdiction and venue for any such claim or dispute shall be the state and federal courts located in New Castle County, Delaware, and the parties consent to the personal jurisdiction of such courts.
      </p>

      <h2>12. Severability</h2>
      <p>
        If any provision of this Notice is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction, such invalidity, illegality, or unenforceability shall not affect any other provision of this Notice, and this Notice shall be construed as if such invalid, illegal, or unenforceable provision had never been contained herein. To the extent permitted by applicable law, the parties agree that any such invalid, illegal, or unenforceable provision shall be reformed to the minimum extent necessary to make it valid, legal, and enforceable while preserving the original intent of the parties.
      </p>

      <h2>13. No Waiver</h2>
      <p>
        No failure or delay by Closing Theory in exercising any right, power, or remedy under this Notice shall operate as a waiver thereof, nor shall any single or partial exercise of any right, power, or remedy preclude any other or further exercise thereof or the exercise of any other right, power, or remedy. All rights and remedies under this Notice are cumulative and are not exclusive of any other rights or remedies provided by law.
      </p>

      <h2>14. Changes to This Notice</h2>
      <p>
        We may update this Notice from time to time to reflect changes in the Service, our data sources, our practices, legal requirements, or operational needs. When we do, we will update the "Last Updated" date above. Your continued use of Househunt after an updated Notice becomes effective constitutes your acknowledgment of the revised Notice, to the extent permitted by law.
      </p>

      <h2>15. Contact</h2>
      <p>If you have questions about this Real Estate Data Notice, please contact us at:</p>
      <ul className="contact-info">
        <li>Closing Theory Studios, Inc.</li>
        <li><a href="mailto:legal@closingtheory.com">legal@closingtheory.com</a></li>
      </ul>
    </div>
  );
};

export default RealEstateDataNotice;
